import { useMemo } from 'react'
import { useVisitState } from '../../hooks/useTabVisit'
import { Navigate, useSearchParams } from 'react-router-dom'
import { fbt, useLocaleContext } from 'fbtee'
import { pageTitle, useDocumentTitle } from '../../hooks/useDocumentTitle'
import { useAuth } from '../../hooks/useAuth'
import { attendingEventIds, useEventsByIds } from '../../hooks/useEventsByIds'
import { useUserEventInterests } from '../../hooks/useUserEventInterests'
import { useFrozenValue } from '../../hooks/useFrozenValue'
import { useArtistInterests } from '../../hooks/useArtistInterests'
import { useFriendArtistInterests } from '../../hooks/useFriendArtistInterests'
import { useArtistsByIds } from '../../hooks/useArtistsByIds'
import { useGuestGuard } from '../../hooks/useGuestGuard'
import { useFilteredEventsByStatus } from '../../hooks/useFilteredEventsByStatus'
import { usePageLoadTrace } from '../../lib/perf'
import { interestPrompt } from '../../lib/guestPrompts'
import { track } from '../../lib/analytics'
import { PageLayout } from '../../components/PageLayout'
import { ProfileHero } from '../../components/profile/ProfileHero'
import { ProfileStats } from '../../components/profile/ProfileStats'
import { buildProfileStats } from '../../components/profile/buildProfileStats'
import { Skeleton } from '../../components/ui/skeleton'
import { ProfilePillNav } from '../../components/profile/ProfilePillNav'
import { PROFILE_TABS, type ProfileTab } from '../../components/profile/profileTabs'
import { ProfileEventsView } from '../../components/profile/ProfileEventsView'
import { ProfileArtistsView } from '../../components/profile/ProfileArtistsView'
import { SelfProfileHero } from './SelfProfileHero'
import { GuestProfileHero, GuestProfileUpsell } from './GuestProfile'
import { useSocialEventData } from '../../hooks/useSocialEventData'
import { useFriendships } from '../../hooks/useFriendships'
import { useBlockedUsers } from '../../contexts/BlockedUsersContext'
import { useTransitionNavigate } from '../../lib/viewTransition'

const NO_IDS: string[] = []

// Account and Admin used to be profile tabs; old links land on their pages now.
const MOVED_TABS: Record<string, string> = { account: '/settings', admin: '/admin' }

export default function ProfilePage() {
  useDocumentTitle(pageTitle(fbt('Profile', 'Browser tab title for the profile page')))
  const { locale } = useLocaleContext()
  const { user, signedInUid, loading: authLoading } = useAuth()
  usePageLoadTrace('profile_page_to_content', !authLoading)
  const [searchParams, setSearchParams] = useSearchParams()
  const tabParam = searchParams.get('tab')
  const initialTab = PROFILE_TABS.includes(tabParam as ProfileTab) ? (tabParam as ProfileTab) : 'events'
  const [tab, setTab] = useVisitState<ProfileTab>(initialTab)

  const { eventStatuses: myStatuses, loading: interestsLoading } = useUserEventInterests(signedInUid)
  const { facepileData, mergedProfiles, friendIds } = useSocialEventData(myStatuses)
  const { guard } = useGuestGuard()
  const navigate = useTransitionNavigate()
  const friendships = useFriendships(signedInUid)
  const { isBlocked } = useBlockedUsers()
  const myEventIds = useMemo(() => attendingEventIds(myStatuses), [myStatuses])
  const { events, pending: eventsLoading } = useEventsByIds(myEventIds)

  const liveEventSections = useFilteredEventsByStatus(events, myStatuses)
  const { goingEvents, interestedEvents, pastGoingEvents } = useFrozenValue(
    liveEventSections,
    !eventsLoading && !interestsLoading,
    user?.uid,
  )

  const { interestedArtistIds, loading: artistInterestsLoading, setArtistInterest } =
    useArtistInterests(signedInUid)
  const guardedSetArtist = guard(
    (artistId: string, marked: boolean) => setArtistInterest(artistId, marked),
    interestPrompt('artist_interest'),
  )
  const myArtistIds = useMemo(() => [...interestedArtistIds], [interestedArtistIds])
  // Keep loading artists removed this visit — their rows stay (dimmed) until the next one.
  const visitArtistIds = useFrozenValue(myArtistIds, !artistInterestsLoading, user?.uid)
  const fetchArtistIds = useMemo(
    () => [...new Set([...visitArtistIds, ...myArtistIds])],
    [visitArtistIds, myArtistIds],
  )
  const { artists, loading: artistsLoading } = useArtistsByIds(fetchArtistIds)
  // Every friend's artist interests, app-wide — only worth reading on the Artists tab.
  const { artistUserIds } = useFriendArtistInterests(tab === 'artists' ? friendIds : NO_IDS)

  const isGuest = !signedInUid
  const eventsPending = eventsLoading || interestsLoading

  function handleTabChange(t: ProfileTab) {
    setTab(t)
    setSearchParams(t === 'events' ? {} : { tab: t }, { replace: true })
    track('profile_tab_changed', { tab: t })
  }

  if (tabParam && MOVED_TABS[tabParam]) return <Navigate to={MOVED_TABS[tabParam]} replace />

  const stats = buildProfileStats({
    pastEvents: pastGoingEvents,
    artistCount: interestedArtistIds.size,
    onEventsClick: () => handleTabChange('events'),
    onArtistsClick: () => handleTabChange('artists'),
    friendCount: isGuest ? undefined : friendships.friendIds.filter(uid => !isBlocked(uid)).length,
    onFriendsClick: () => navigate('/friends'),
  })

  return (
    <PageLayout hideOnMobile>
      {authLoading && (
        <ProfileHero loading avatar={null}>
          <Skeleton className="h-7 w-32" />
          <ProfileStats loading placeholderCount={4} />
        </ProfileHero>
      )}
      {!authLoading && isGuest && <GuestProfileHero />}
      {!authLoading && !isGuest && (
        <SelfProfileHero stats={stats} statsLoading={eventsPending || artistInterestsLoading} />
      )}

      <ProfilePillNav tab={tab} onTabChange={handleTabChange} />

      {tab === 'events' && (
        <ProfileEventsView
          goingEvents={goingEvents}
          interestedEvents={interestedEvents}
          pastEvents={pastGoingEvents}
          myStatuses={myStatuses}
          facepileData={facepileData}
          profiles={mergedProfiles}
          currentUserId={user?.uid ?? null}
          loading={authLoading || (!isGuest && eventsPending)}
          locale={locale}
          isSelf
          empty={isGuest ? <GuestProfileUpsell kind="events" /> : undefined}
        />
      )}

      {tab === 'artists' && (
        <ProfileArtistsView
          artistIds={myArtistIds}
          artists={artists}
          myInterestedArtistIds={interestedArtistIds}
          onSetArtist={guardedSetArtist}
          artistUserIds={artistUserIds}
          userProfiles={mergedProfiles}
          viewedUserId={user?.uid ?? null}
          loading={authLoading || artistInterestsLoading || artistsLoading}
          isSelf
          empty={isGuest ? <GuestProfileUpsell kind="artists" /> : undefined}
        />
      )}
    </PageLayout>
  )
}
